import * as Sentry from "@sentry/remix";
import posthog from "posthog-js";
/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";

// Only initialize Sentry in production to avoid sending local dev errors
const isProduction = process.env.NODE_ENV === "production";

if (isProduction) {
  const sentryDsn =
    (window as unknown as { ENV?: Record<string, string> }).ENV?.SENTRY_DSN ||
    "https://cd7847ec1fa0966145399ddcd6a5a6f4@o4506551058759680.ingest.us.sentry.io/4507813698666496";

  Sentry.init({
    dsn: sentryDsn,
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,

    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
    ],
  });
}

// Initialize PostHog after DOM is ready
function initPostHog() {
  const env = (window as unknown as { ENV?: Record<string, string> }).ENV;
  const apiKey = env?.POSTHOG_API_KEY;
  const host = env?.POSTHOG_HOST || "https://us.i.posthog.com";

  if (apiKey) {
    posthog.init(apiKey, {
      api_host: host,
      autocapture: true, // Enable automatic event capture
      capture_pageview: true, // Enable automatic pageview tracking
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      loaded: () => {
        if (process.env.NODE_ENV === "development") {
          console.log("[Analytics] PostHog initialized");
        }
      },
    });
  }
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>
  );

  // Initialize PostHog after hydration is fully committed. PostHog autocapture
  // synchronously injects a <script> into <body>, which shifts SSR DOM children
  // out from under React's in-progress hydration and causes "Expected server
  // HTML to contain a matching <link> in <body>" mismatches in <Scripts />.
  // Defer to the next macrotask so React's hydration commit runs first.
  if (typeof window !== "undefined") {
    if (document.readyState === "complete") {
      setTimeout(initPostHog, 0);
    } else {
      window.addEventListener("load", () => setTimeout(initPostHog, 0), { once: true });
    }
  }
});